import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ParticleCanvas from "./ParticleCanvas";
import { getPlatformStats, type PlatformStats } from "../../../services/platform";
import RIcon from "../../../components/RIcon";

// ─── conversation pairs (每轮展示 2 问 2 答) ─────────────────────────
const PAIRS = [
  {
    q1: "请分析ICH Q10的核心质量体系要求",
    a1: "ICH Q10核心要素包括质量手册、流程管理、CAPA体系与变更控制，贯穿产品全生命周期，确保持续合规。",
    src1: [{ icon: "ri-file-pdf-line", name: "ICH_Q10.pdf" }, { icon: "ri-file-word-line", name: "GMP手册.docx" }],
    q2: "如何建立有效的CAPA系统？",
    a2: "有效CAPA需覆盖根本原因分析、行动计划制定、实施跟踪和效果验证四个阶段，并设置关闭审批流程。",
    src2: [{ icon: "ri-file-pdf-line", name: "CAPA规程v2.pdf" }, { icon: "ri-file-word-line", name: "质量手册.docx" }],
  },
  {
    q1: "临床试验数据完整性如何保障？",
    a1: "遵循ALCOA+原则，结合电子签名、审计追踪与权限管控，定期自检确保数据可追溯且不可篡改。",
    src1: [{ icon: "ri-file-pdf-line", name: "GCP指南.pdf" }, { icon: "ri-file-text-line", name: "临床SOP-DI.docx" }],
    q2: "新药注册申报需要哪些核心文件？",
    a2: "需准备CTD格式申报资料，涵盖行政信息、质量研究、非临床与临床数据五大模块，附上研究者手册。",
    src2: [{ icon: "ri-file-pdf-line", name: "CTD申报指南.pdf" }, { icon: "ri-folder-line", name: "注册法规汇编" }],
  },
];

// ─── phase ───────────────────────────────────────────────────────────
type Phase =
  | "u1-in" | "dots1" | "stream1" | "src1-in"   // 第一轮
  | "inter"                                        // 轮间停顿
  | "u2-in" | "dots2" | "stream2" | "src2-in"   // 第二轮
  | "hold" | "fade-out";                           // 结束

// ─── AIChatDemo ───────────────────────────────────────────────────────
function AIChatDemo() {
  const [pairIdx, setPairIdx] = useState(0);
  const [phase, setPhase]     = useState<Phase>("u1-in");
  const [text1, setText1]     = useState("");
  const [text2, setText2]     = useState("");
  const [src1, setSrc1]       = useState(false);
  const [src2, setSrc2]       = useState(false);
  const [visible, setVisible] = useState(true);

  const timerRef  = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const streamRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const charRef   = useRef(0);

  const pair = PAIRS[pairIdx];
  const clearTimer  = () => { if (timerRef.current)  clearTimeout(timerRef.current); };
  const clearStream = () => { if (streamRef.current) clearInterval(streamRef.current); };

  // ── stream helper ──────────────────────────────────────────────────
  const startStream = (
    fullText: string,
    setter: (s: string) => void,
    onDone: () => void,
  ) => {
    charRef.current = 0;
    setter("");
    streamRef.current = setInterval(() => {
      charRef.current += 1;
      setter(fullText.slice(0, charRef.current));
      if (charRef.current >= fullText.length) {
        clearStream();
        timerRef.current = setTimeout(onDone, 220);
      }
    }, 26);
  };

  useEffect(() => {
    clearTimer();
    clearStream();

    switch (phase) {
      case "u1-in":   timerRef.current = setTimeout(() => setPhase("dots1"),   880); break;
      case "dots1":   timerRef.current = setTimeout(() => setPhase("stream1"), 850); break;
      case "stream1": startStream(pair.a1, setText1, () => setPhase("src1-in")); break;
      case "src1-in": setSrc1(true);  timerRef.current = setTimeout(() => setPhase("inter"),   280); break;
      case "inter":   timerRef.current = setTimeout(() => setPhase("u2-in"),   1100); break;
      case "u2-in":   timerRef.current = setTimeout(() => setPhase("dots2"),   880); break;
      case "dots2":   timerRef.current = setTimeout(() => setPhase("stream2"), 850); break;
      case "stream2": startStream(pair.a2, setText2, () => setPhase("src2-in")); break;
      case "src2-in": setSrc2(true);  timerRef.current = setTimeout(() => setPhase("hold"),    280); break;
      case "hold":    timerRef.current = setTimeout(() => setPhase("fade-out"), 2200); break;
      case "fade-out":
        setVisible(false);
        timerRef.current = setTimeout(() => {
          setPairIdx(i => (i + 1) % PAIRS.length);
          setText1(""); setText2(""); setSrc1(false); setSrc2(false);
          setVisible(true);
          setPhase("u1-in");
        }, 480);
        break;
    }
    return () => { clearTimer(); clearStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pairIdx]);

  // ── visibility helpers ─────────────────────────────────────────────
  const phaseOrder: Phase[] = ["u1-in","dots1","stream1","src1-in","inter","u2-in","dots2","stream2","src2-in","hold","fade-out"];
  const phaseGte = (target: Phase) => phaseOrder.indexOf(phase) >= phaseOrder.indexOf(target);

  const fading = phase === "fade-out";

  return (
    <div className="space-y-3 transition-opacity duration-500" style={{ opacity: visible ? 1 : 0 }}>

      {/* ── Round 1: User ── */}
      <div
        className="flex justify-end transition-all duration-500"
        style={{ opacity: fading ? 0 : 1, transform: phase === "u1-in" ? "translateX(10px)" : "translateX(0)" }}
      >
        <div className="bg-[#0EA5A0] text-white text-xs px-3 py-2 rounded-2xl rounded-tr-sm max-w-[82%] leading-relaxed">
          {pair.q1}
        </div>
      </div>

      {/* ── Round 1: AI ── */}
      {phaseGte("dots1") && (
        <div className="flex justify-start" style={{ opacity: fading ? 0 : 1 }}>
          <div className="bg-white/20 text-white text-xs px-3 py-2 rounded-2xl rounded-tl-sm max-w-[88%] leading-relaxed min-h-[32px]">
            {phase === "dots1" ? (
              <span className="flex gap-1 items-center h-4">
                {[0,150,300].map(d => <span key={d} className="w-1.5 h-1.5 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
              </span>
            ) : (
              <span>
                {text1}
                {phase === "stream1" && <span className="inline-block w-[2px] h-[12px] bg-[#F59E0B] ml-[1px] align-middle animate-pulse" />}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Round 1: Sources ── */}
      <div
        className="flex flex-wrap gap-1.5 overflow-hidden transition-all duration-500"
        style={{ maxHeight: src1 ? "48px" : "0px", opacity: src1 && !fading ? 1 : 0 }}
      >
        {pair.src1.map(s => (
          <span key={s.name} className="bg-white/10 text-white/70 text-xs px-2 py-0.5 rounded-full border border-white/20">
            <i className={`${s.icon} mr-1`} />{s.name}
          </span>
        ))}
      </div>

      {/* ── Divider between rounds ── */}
      {phaseGte("inter") && (
        <div
          className="border-t border-white/10 transition-opacity duration-500"
          style={{ opacity: fading ? 0 : 0.6 }}
        />
      )}

      {/* ── Round 2: User ── */}
      {phaseGte("u2-in") && (
        <div
          className="flex justify-end transition-all duration-500"
          style={{ opacity: fading ? 0 : 1, transform: phase === "u2-in" ? "translateX(10px)" : "translateX(0)" }}
        >
          <div className="bg-[#0EA5A0] text-white text-xs px-3 py-2 rounded-2xl rounded-tr-sm max-w-[82%] leading-relaxed">
            {pair.q2}
          </div>
        </div>
      )}

      {/* ── Round 2: AI ── */}
      {phaseGte("dots2") && (
        <div className="flex justify-start" style={{ opacity: fading ? 0 : 1 }}>
          <div className="bg-white/20 text-white text-xs px-3 py-2 rounded-2xl rounded-tl-sm max-w-[88%] leading-relaxed min-h-[32px]">
            {phase === "dots2" ? (
              <span className="flex gap-1 items-center h-4">
                {[0,150,300].map(d => <span key={d} className="w-1.5 h-1.5 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
              </span>
            ) : (
              <span>
                {text2}
                {phase === "stream2" && <span className="inline-block w-[2px] h-[12px] bg-[#F59E0B] ml-[1px] align-middle animate-pulse" />}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Round 2: Sources ── */}
      <div
        className="flex flex-wrap gap-1.5 overflow-hidden transition-all duration-500"
        style={{ maxHeight: src2 ? "48px" : "0px", opacity: src2 && !fading ? 1 : 0 }}
      >
        {pair.src2.map(s => (
          <span key={s.name} className="bg-white/10 text-white/70 text-xs px-2 py-0.5 rounded-full border border-white/20">
            <i className={`${s.icon} mr-1`} />{s.name}
          </span>
        ))}
      </div>

    </div>
  );
}



// ─── Hero ─────────────────────────────────────────────────────────────
export default function Hero() {
  const [ps, setPs] = useState<PlatformStats | null>(null);
  const [aiOnline, setAiOnline] = useState(false);
  useEffect(() => { getPlatformStats().then(setPs).catch(() => {}); }, []);
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const res = await fetch(`${(import.meta.env.VITE_API_BASE || "/api").replace(/\/+$/, "")}/health`, { method: "GET" });
        if (!cancelled) setAiOnline(res.ok);
      } catch {
        if (!cancelled) setAiOnline(false);
      }
    };
    probe();
    const t = setInterval(probe, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background — 纯 CSS 紫琥珀渐变 + 装饰光斑 (取代原 readdy 深绿蓝图) */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(135deg, #1A1429 0%, #2E1A47 35%, #4C2D6B 65%, #8B3A62 100%)"
      }}>
        <div className="absolute inset-0" style={{
          background:
            "radial-gradient(45% 50% at 15% 25%, rgba(245, 158, 11, 0.28), transparent 60%)," +
            "radial-gradient(50% 60% at 85% 75%, rgba(168, 85, 247, 0.50), transparent 70%)," +
            "radial-gradient(35% 40% at 50% 0%, rgba(255, 255, 255, 0.08), transparent 70%)"
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1A1429]/55" />
      </div>

      {/* Particle Canvas Layer */}
      <ParticleCanvas />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-16 flex flex-col lg:flex-row items-center gap-12">
        {/* Left Content */}
        <div className="flex-1 text-center lg:text-left">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/90 text-xs font-medium tracking-wide">医疗知识智能化管理平台</span>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
            智慧知识，
            <br />
            驱动医疗创新
          </h1>

          {/* Subtitle */}
          <p className="text-white/75 text-lg font-light tracking-wider mb-10">
            统一知识库 · 智能问答 · 文档生成
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 bg-[#F59E0B] text-white px-8 h-12 rounded-full text-sm font-semibold hover:bg-[#D97706] transition-all hover:translate-y-[-2px] whitespace-nowrap"
            >
              立即登录
              <RIcon name="arrow-right-line" />
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white px-8 h-12 rounded-full text-sm font-semibold hover:bg-white/25 transition-all whitespace-nowrap"
            >
              申请注册
            </Link>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap gap-8 mt-12 justify-center lg:justify-start">
            {[
              { value: ps ? `${ps.indexed_files.toLocaleString()}+` : "...", label: "文档索引" },
              { value: ps ? `${ps.total_chats.toLocaleString()}+` : "...", label: "累计问答" },
              { value: ps ? `${ps.total_users}+` : "...", label: "活跃用户" },
            ].map((s) => (
              <div key={s.label} className="text-center lg:text-left">
                <div className="text-2xl font-extrabold text-[#AAFF45]">{s.value}</div>
                <div className="text-white/60 text-xs mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Floating Card */}
        <div className="flex-shrink-0 w-full max-w-sm lg:max-w-md">
          <div className="relative">
            {/* Main Card */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 shadow-2xl">
              {/* Card Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-white text-sm font-medium">AI 智能问答</span>
                <span className="ml-auto text-white/50 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  实时在线
                </span>
              </div>

              {/* Dynamic Chat */}
              <div className="min-h-[230px]">
                <AIChatDemo />
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -top-3 -right-3 bg-white rounded-xl px-3 py-2 shadow-lg">
              <div className="text-[#0EA5A0] text-xs font-bold">文档索引</div>
              <div className="text-white text-lg font-extrabold leading-none">
                {ps ? ps.indexed_files.toLocaleString() : "..."}
              </div>
            </div>

            {/* Floating Status：以真实 /api/health 为准 */}
            <div className={`absolute -bottom-3 -left-3 ${aiOnline ? "bg-[#0EA5A0]" : "bg-red-500"} text-white rounded-xl px-3 py-2 shadow-lg flex items-center gap-2`}>
              <span className={`w-2 h-2 ${aiOnline ? "bg-[#F59E0B]" : "bg-white"} rounded-full ${aiOnline ? "animate-pulse" : ""}`} />
              <span className="text-xs font-medium">{aiOnline ? "在线服务" : "服务异常"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 z-10">
        <span className="text-xs">向下探索</span>
        <RIcon name="arrow-down-line" className="text-lg animate-bounce" />
      </div>
    </section>
  );
}
